<template>
    <div>
        <Loader :loader="loader" />
        <div class="app" v-if="!loader">
            <NavBar :currentUser="currentUser" />
            <div class="app-body">
                <AppSidebar fixed>
                    <SidebarHeader />
                    <SidebarForm />
                    <SidebarNav :nav-items="getNavItems"></SidebarNav>
                    <SidebarFooter />
                    <SidebarMinimizer />
                </AppSidebar>
                <main class="main">
                    <Breadcrumb :list="list" class="mb-0" />
                    <div class="margin-container" :class="{ 'no-margin': noMargin }">
                        <router-view></router-view>
                    </div>
                </main>
                <AppAside fixed>
                    <!--aside-->
                    <component :is="sharedState.asideComponent" v-bind="sharedState.asideProps" />
                </AppAside>
            </div>
            <Cookies v-if="!userAcceptedCookiesUse" @userAccepted="checkIfUserAcceptedCookiesUse" />
            <TheFooter>
                <!--footer-->
                <div>
                    <a href="https://coblue.com.br">Copyright {{ new Date().getFullYear() }} CoBlue</a>
                    - Gestão Contínua de Performance
                </div>
            </TheFooter>
        </div>
    </div>
</template>

<script>
import api from '@/services/api'
import oauth from '@/services/oauth-api'
import AuthService from '@/services/auth'
import Loader from '@/views/components/loader'
import Cookies from '@/views/cookies'
import { setUserPermissions } from '@/services/user-permissions'
import { setUserData } from '@/services/user-data'
import { systemLocaleMixin } from '@/mixins'
import getSidebarMenuItems from '@/_nav'
import {
    SidebarToggler,
    Sidebar as AppSidebar,
    SidebarFooter,
    SidebarForm,
    SidebarHeader,
    SidebarMinimizer,
    SidebarNav,
    Aside as AppAside,
    AsideToggler,
    Footer as TheFooter,
    Breadcrumb,
} from '@coreui/vue'
import Aside from '@/shared/aside'
import NavBar from './Header/Navbar'
import Notifications from '@/views/notifications'
import { deleteAllCookiesMixin } from '@/mixins'
import { EventBuzz } from '@/main'
import { companyStorage } from '@/services/storage'

export default {
    name: 'DefaultContainer',
    mixins: [systemLocaleMixin, deleteAllCookiesMixin],
    components: {
        NavBar,
        AsideToggler,
        AppSidebar,
        AppAside,
        TheFooter,
        Breadcrumb,
        SidebarForm,
        SidebarFooter,
        SidebarToggler,
        SidebarHeader,
        SidebarNav,
        SidebarMinimizer,
        Notifications,
        Loader,
        Cookies,
    },
    data() {
        return {
            loader: true,
            currentUserReady: false,
            currentUser: {},
            sharedState: Aside.state,
            pendingEvaluations: null,
            userAcceptedCookiesUse: false,
            noMargin: false,
        }
    },
    created() {
        EventBuzz.$on('toggle-no-margin', this.handleNoMargin)

        this.getCurrentUserFromAPI().then(() => {
            const { email, name, company, sections } = this.currentUser

            const { url_cognito } = company
            companyStorage.setCompanyData(url_cognito)

            const movideskChatUserInfo = {
                email,
                emptySubject: false,
                name,
                startChat: false,
            }

            if (this.checkIfUserAcceptedCookiesUse()) {
                let stateCheck = setInterval(() => {
                    if (document.readyState === 'complete') {
                        // eslint-disable-next-line
                        movideskLogin(movideskChatUserInfo)
                        clearInterval(stateCheck)
                    }
                }, 200)
            }

            const permissions = {
                modules: {
                    has_kpi: this.currentUser.has_kpi && this.currentUser.company.has_kpi,
                    has_goal: this.currentUser.has_goal && this.currentUser.company.has_goal,
                    has_talk: this.currentUser.has_talk && this.currentUser.company.has_talk,
                    has_value: this.currentUser.has_value && this.currentUser.company.has_value,
                    has_okr: this.currentUser.has_okr && this.currentUser.company.has_okr,
                    has_pdi: this.currentUser.has_pdi && this.currentUser.company.has_pdi,
                    has_strategy: this.currentUser.company.has_strategy,
                    has_quiz: this.currentUser.company.has_quiz,
                    has_enps: this.currentUser.company.has_enps,
                    has_daily: this.currentUser.company.has_daily,
                },
                current_event_permissions: {
                    goals: this.currentUser.permissions.goals,
                    values: this.currentUser.permissions.values,
                },
                is_manager: this.currentUser.is_manager,
                is_admin: this.currentUser.role.name === 'administrator',
                is_god: this.currentUser.role.name === 'god',
                is_local_administrator: this.currentUser.role.name === 'local_administrator',
                is_okr_leader: this.currentUser.is_okr_leader,
                is_values_master: this.currentUser.is_values_master,
                is_target_master: this.currentUser.is_target_master,
                has_360_visualization: this.currentUser.has_360_visualization,
                has_admin_access: this.currentUser.has_admin_access,
                is_allowed_to_access_manager_profile: this.currentUser.is_allowed_to_access_manager_profile,
                can_download_users: this.canDownloadUsers,
                can_upload_users: this.canUploadUsers,
                can_upload_targets: this.canUploadTargets,
            }

            const userData = {
                id: this.currentUser.id,
                name: this.currentUser.name,
                company_id: this.currentUser.company_id,
                email: this.currentUser.email,
                createdAt: this.currentUser.created_at,
            }

            setUserPermissions(permissions)
            setUserData(userData)

            this.$store.dispatch('initPusher', this.currentUser)

            this.filterSectionsByType(sections, 'department')
            this.filterSectionsByType(sections, 'occupation_group')

            this.loader = false
        })
    },
    methods: {
        getCurrentUserFromAPI() {
            return api
                .get('/users/current', {
                    params: {
                        transform: 'role',
                        with:
                            'subordinates;' +
                            'valuers;' +
                            'manager.subordinates;' +
                            'role;' +
                            'company;' +
                            'evaluatorCommittees.eventPermissions.operation;' +
                            'events.eventHasCriteria;' +
                            'eventUser;' +
                            'editNetworks;' +
                            'sections',
                        append: 'has_roles,has_modules,strategy_viewer',
                    },
                })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    if (data.status.name === 'disconnected') {
                        return this.redirectToLogin()
                    }

                    this.$store.dispatch('addCurrentUser', data)
                    this.currentUser = data
                    this.setSystemCurrentLocale(this.currentUser.language)
                })
                .catch((e) => {
                    console.log(e)
                })
        },
        redirectToLogin() {
            this.authService.logout()
        },
        checkIfUserAcceptedCookiesUse() {
            this.userAcceptedCookiesUse = this.getBooleanFromLocalStorage(this.acceptedCookiesUseKey)
            const acceptedExternalCookiesUse = this.getBooleanFromLocalStorage(this.acceptedExternalCookiesUseKey)
            if (!acceptedExternalCookiesUse && this.userAcceptedCookiesUse) {
                this.deleteAllCookies()
                return false
            }
            return true
        },
        getBooleanFromLocalStorage(key) {
            return !!JSON.parse(localStorage.getItem(key))
        },
        handleNoMargin(state) {
            this.noMargin = state
        },
        translateBoolean(boolean) {
            return boolean ? 'Yes' : 'No'
        },
        filterSectionsByType(sections, type) {
            return sections.filter((section) => section.type === type)
        },
        getSectionsName(sections) {
            return sections.map((section) => section.name).join()
        },
        getActiveModulesNames(has_modules) {
            const modules = {
                has_okr: 'okr',
                has_talk: 'talk',
                has_value: 'values',
                has_kpi: 'kpi',
                has_goal: 'target',
                has_pdi: 'pdi',
                has_mini_curriculum: 'mini_curriculum',
                has_career_objective: 'career_objective',
                has_enps: 'enps',
                has_strategy: 'strategy',
                has_quiz: 'humor',
            }
            return has_modules.map((key) => modules[key])
        },
    },
    computed: {
        name() {
            return this.$route.name
        },
        list() {
            return this.$route.matched.filter((route) => route.name || route.meta.label)
        },
        currentUserInfos() {
            const { id, name, email, picture } = this.currentUser
            return { id, name, email, picture }
        },
        currentUserPermissions() {
            const {
                company,
                has_kpi,
                has_okr,
                has_talk,
                has_value,
                has_goal,
                is_manager,
                is_allowed_to_access_manager_profile,
                permissions,
                role,
                is_values_master,
                is_target_master,
                has_360_visualization,
                has_admin_access,
                is_okr_leader,
            } = this.currentUser
            return {
                hasStrategy: company.has_strategy,
                hasKpi: has_kpi,
                hasOkr: has_okr,
                hasTalk: has_talk,
                hasValues: has_value,
                hasTarget: has_goal,
                isAdmin: role.name == 'administrator',
                isGod: role.name == 'god',
                isManager: is_manager,
                isLocalAdministrator: role.name == 'local_administrator',
                isOkrLeader: is_okr_leader,
                isAllowedToAccessManagerProfile: is_allowed_to_access_manager_profile,
                isValuesMaster: is_values_master,
                isTargetMaster: is_target_master,
                has360Visualization: has_360_visualization,
                hasAdminAccess: has_admin_access,
                permissions: {
                    section: permissions.section,
                    user: permissions.user,
                    period: permissions.period,
                    company: permissions.company,
                    role,
                },
                eventPermissions: this.currentUser.permissions.goals,
            }
        },
        getNavItems() {
            return getSidebarMenuItems(
                this.currentUser,
                this.currentUserPermissions,
                this.currentUser.id,
                this.pendingEvaluations
            )
        },
        canDownloadUsers() {
            return this.currentUser.role.name === 'god' || this.currentUser.role.name === 'administrator'
        },
        canUploadUsers() {
            return (
                this.currentUser.role.name === 'god' ||
                (this.currentUser.company.url_prefix != null && this.currentUser.role.name === 'administrator')
            )
        },
        canUploadTargets() {
            return this.canUploadUsers
        },
        authService() {
            return AuthService(api, oauth)
        },
    },
}
</script>

<style lang="css">
.fa-question-circle {
    color: #009ada;
}

.margin-container {
    margin: 0;
}

@media screen and (min-width: 768px) {
    .margin-container {
        margin: 0.5rem;
    }
}

.no-margin {
    margin: 0;
}
</style>
